import { useWindowSize } from "hooks/useWindowSize";
import { DueDate } from "components/design/DueDate";
import { InvoiceItemGroup } from "components/design/InvoiceItemGroup";
import { ToggleNumbers } from "components/design/ToggleNumbers";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { CopyIcon } from "components/icons/CopyIcon";
import { DeleteIcon } from "components/icons/delete";
import { EnterIcon } from "components/icons/EnterIcon";
import { InfoFilledSmall } from "components/icons/InfoFilledSmall";
import { NumericInput } from "components/NumericInput/NumericInput";
import { PriceInput } from "components/PriceInput/PriceInput";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import {
  Field,
  FieldArray,
  FieldProps,
  FormikErrors,
  useFormikContext,
} from "formik";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import {
  UpdatePayload,
  useUpdateBillOrRequestMutation,
} from "store/apis/vendorBills";
import { VendorBill } from "types/Models/vendorBills";
import { debounce } from "utils/debouncing";
import MerchantSelector from "./MerchantSelector";
import { AddressComponents } from "components/AddressComponent/AddressComponent";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { LockedDatePicker } from "components/LockedDatePicker/LockedDatePicker";
import { useGetCurrenciesQuery } from "store/apis/invoices";
import { ReactNode, useMemo, useState } from "react";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { USD } from "constants/currency";
import { useModal } from "hooks/useModal";
import Modal from "components/DesignSystem/Modal/Modal";
import { InvoiceItemType, Label } from "components/InvoiceForm/InvoiceForm";
import { formatDate } from "utils/formatDate";
import { AddLineItemButton } from "components/LineItems/AddLineItemButton";
import { LineItemTable } from "components/LineItems/LineItemTable";
import { LineItemUI } from "components/LineItems/LineItemUI";
import FocusBlock from "components/DesignSystem/FocusBlock/FocusBlock";
import { FOCUS_BLOCK_PLACEMENT } from "constants/focusBlock";
import { BackendError } from "types/utils/error";
import { useToast } from "hooks/useToast";
import {
  ASSET_CATEGORY_IDENTIFIER,
  EXPENSE_CATEGORY_IDENTIFIER,
} from "constants/categoryIdentifier";
import { InvoiceItem } from "types/Models/invoice";
import { useGetMerchantByIdQuery } from "store/apis/merchant";
import { useGetInfiniteVendorsInfiniteQuery } from "store/apis/vendors";
import { MergeMerchants } from "components/Vendors/MergeMerchants";
import { useDispatch } from "react-redux";
import { setMerchantToMerge } from "store/slices/vendors";
import { Vendor } from "types/Models/payroll";
import { CUSTOMER_AI_STATUS } from "constants/vendorBills";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { Merchant } from "types/Models/merchant";

type BillConsoleProps = {
  bill: VendorBill;
  onChange?: (value: Partial<UpdatePayload>) => void;
  groupId?: string;
  entityId?: string;
  setOverlayActiveItem?: (id: string | null) => void;
  showCategory?: boolean;
  isBillLineItem?: boolean;
  showTotals?: boolean;
};

export const VendorDetails = ({
  bill,
  onChange,
  disabled,
}: BillConsoleProps & { disabled?: boolean }) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { errors, setFieldValue } = useFormikContext<UpdatePayload>();
  const { entity_merchant_data } = errors || {};
  const { data: selectedMerchant, isLoading } = useGetMerchantByIdQuery({
    groupId: bill.group_id,
    merchantId: bill.entity_merchant_data?.uuid || "",
  });

  const dispatch = useDispatch();

  const mergeModal = useModal();

  const onMergeModalOpen = () => {
    if (bill.entity_merchant_data) {
      dispatch(setMerchantToMerge([bill.entity_merchant_data.uuid]));
    }
    mergeModal.open();
  };

  let note: ReactNode = "";

  if (bill.customer_ai_status === CUSTOMER_AI_STATUS.CREATED) {
    note = (
      <div className="t-flex t-items-center t-gap-0.5">
        <span>New vendor created from bill.</span>
        <Button
          onClick={onMergeModalOpen}
          type="button"
          customType="link"
          size="small"
        >
          Merge if it's a duplicate
        </Button>
      </div>
    );
  }

  if (bill.customer_ai_status === CUSTOMER_AI_STATUS.USED_EXISTING) {
    note = (
      <div className="t-text-dark_green">
        Vendor matched successfully, change vendor if needed
      </div>
    );
  }

  if (
    bill.customer_ai_status === CUSTOMER_AI_STATUS.FAILED_TO_CREATE_OR_MATCH
  ) {
    note = "Vendor not detected, please select or create one";
  }

  const {
    data: vendorsData,
    fetchNextPage: loadNext,
    isLoading: merchantsLoading,
  } = useGetInfiniteVendorsInfiniteQuery(
    {
      groupId,
      entityId,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const merchants = useMemo(() => {
    const allVendors =
      vendorsData?.pages?.reduce((acc, page) => {
        return [...acc, ...(page.merchants || [])];
      }, [] as Vendor[]) || [];
    return {
      merchants: allVendors,
    };
  }, [vendorsData?.pages]);

  const onMergeSuccess = (mergedMerchant: {
    uuid: string;
    merchant_id: string;
    name: string;
  }) => {
    setFieldValue("entity_merchant_data", {
      uuid: mergedMerchant.merchant_id,
      name: mergedMerchant.name,
    });
  };

  const merchantOptions = [
    {
      uuid: bill.entity_merchant_data?.uuid || "",
      name: bill.entity_merchant_data?.name || "",
      logo: "",
    },
    ...merchants?.merchants.filter(
      (merchant) => merchant.uuid !== bill.entity_merchant_data?.uuid
    ),
  ];

  return (
    <InvoiceItemGroup title="Vendor Details" required>
      <MerchantSelector
        required
        type={
          (
            entity_merchant_data as FormikErrors<
              VendorBill["entity_merchant_data"]
            >
          )?.uuid
            ? "error"
            : undefined
        }
        isDisabled={disabled}
        isLoading={isLoading}
        withForm
        name="entity_merchant_data.uuid"
        isClearable={false}
        value={
          bill?.entity_merchant_data
            ? {
                label: selectedMerchant?.name || bill.entity_merchant_data.name,
                value: bill.entity_merchant_data.uuid,
              }
            : undefined
        }
        placeholder="Select your vendor"
        size="small"
        onMerchantSelect={(merchant) => {
          setFieldValue("currency", merchant.currency);
        }}
        note={note}
      />

      {merchants?.merchants && bill.entity_merchant_data && (
        <MergeMerchants
          show={mergeModal.isOpen}
          closeModal={mergeModal.close}
          groupId={groupId}
          onSuccess={onMergeSuccess}
          merchants={merchantOptions as Merchant[]}
          onMenuScrollToBottom={loadNext}
        />
      )}
    </InvoiceItemGroup>
  );
};

export const VendorAddress = ({ bill, onChange }: BillConsoleProps) => {
  const { setFieldValue } = useFormikContext<UpdatePayload>();
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();

  return (
    <InvoiceItemGroup title="Vendor Address" required>
      <AddressComponents
        formPrefix=""
        entityId={entityId}
        groupId={groupId}
        selectedAddressId={bill.merchant_address?.uuid || ""}
        label="Address"
        onAddressSelect={(address) => {
          setFieldValue("merchant_address", address);
        }}
        hideClear
      />
    </InvoiceItemGroup>
  );
};

export const BillDetails = ({ bill, onChange }: BillConsoleProps) => {
  const { isMobile } = useWindowSize();
  const { values, setValues, setFieldValue } =
    useFormikContext<UpdatePayload>();
  const currencyModal = useModal();
  const [newCurrency, setNewCurrency] = useState<string | null>(null);

  const { data: currenciesResponse } = useGetCurrenciesQuery();

  const currencyOptions = useMemo(() => {
    if (!currenciesResponse) return [];

    return Object.entries(currenciesResponse).map(([code, name]) => ({
      label: `${code} - ${name}`,
      value: code,
    }));
  }, [currenciesResponse]);

  const currentCurrency = currencyOptions.find(
    (currency) => currency.value === values.currency
  );

  const invoiceDate = bill.bill_date;

  const dates = useMemo(
    () => ({
      INVOICE_DATE: invoiceDate ? dayjs(invoiceDate) : null,
      NET_30: dayjs(invoiceDate).add(30, "days"),
      NET_60: dayjs(invoiceDate).add(60, "days"),
      NET_90: dayjs(invoiceDate).add(90, "days"),
    }),
    [invoiceDate]
  );

  const dueDate = values.due_date;

  const options = useMemo(() => {
    return [
      ...(invoiceDate
        ? [
            {
              label: <Label info="Due on receipt">Invoice date</Label>,
              value: dayjs(dates.INVOICE_DATE).format(YYYY_MM_DD),
            },
          ]
        : []),
      {
        label: <Label info="Net 30">{formatDate(dates.NET_30)}</Label>,
        value: dayjs(dates.NET_30).format(YYYY_MM_DD),
      },
      {
        label: <Label info="Net 60">{formatDate(dates.NET_60)}</Label>,
        value: dayjs(dates.NET_60).format(YYYY_MM_DD),
      },
      {
        label: <Label info="Net 90">{formatDate(dates.NET_90)}</Label>,
        value: dayjs(dates.NET_90).format(YYYY_MM_DD),
      },
      {
        label: (
          <Label info={dueDate ? "Custom" : ""}>
            {dueDate ? formatDate(dueDate) : "Custom"}
          </Label>
        ),
        value: "CUSTOM",
      },
    ];
  }, [dates, dueDate]);

  return (
    <InvoiceItemGroup title="Bill Details">
      <div className="t-grid t-gap-4 t-grid-cols-2">
        <TextInput
          customSize="small"
          name="title"
          label="Title"
          placeholder="Enter title"
        />
        <TextInput
          required
          customSize="small"
          name="bill_number"
          label="Bill Number"
        />

        <Field name="bill_date" required>
          {({ field }: FieldProps) => {
            return (
              <LockedDatePicker
                {...field}
                name="bill_date"
                customSize="small"
                required
                portalId="bill_date"
                popperPlacement={isMobile ? "bottom-start" : undefined}
                label="Bill Date"
                onDateChange={(date) => {
                  // This will validate form after setting value
                  const savingDate = date ? dayjs(date).format(YYYY_MM_DD) : "";
                  setValues({
                    ...bill,
                    bill_date: savingDate,
                    due_date: null,
                  });
                }}
              />
            );
          }}
        </Field>
        <DueDate
          minDate={bill.bill_date}
          options={options}
          dueDate={bill.due_date}
          name="due_date"
          disabled={!invoiceDate}
        />
        <Combobox
          size="small"
          required
          isClearable={false}
          menuPortalTarget={document.body}
          label="Preferred Currency"
          name="currency"
          placeholder="Select a currency"
          options={currencyOptions}
          value={currentCurrency || null}
          onChange={(option) => {
            if (option instanceof Array || !option) return;

            if (values.currency === option.value) return;

            setNewCurrency(option.value);
            currencyModal.open();
          }}
        />
      </div>
      <Modal.Root
        open={currencyModal.isOpen}
        onOpenChange={currencyModal.close}
      >
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Change Currency</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <p className="t-mb-0 t-text-body-sm t-text-text-60">
              This action will update the invoice currency & adjust the dollar
              amount based on the exchange rate.
              <br />
              Are you sure you want to continue?
            </p>
          </Modal.Body>

          <Modal.FooterButtonGroup>
            <Button customType="secondary" onClick={currencyModal.close}>
              Cancel
            </Button>
            <Button
              customType="primary"
              onClick={() => {
                if (newCurrency) {
                  setFieldValue("currency", newCurrency);
                }
                currencyModal.close();
              }}
            >
              Confirm
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
    </InvoiceItemGroup>
  );
};

export const ItemDetails = ({
  bill,
  onChange,
  groupId,
  entityId,
  setOverlayActiveItem,
  showCategory = true,
  isBillLineItem = true,
  showTotals = false,
}: BillConsoleProps) => {
  const { setFieldValue } = useFormikContext<UpdatePayload>();
  const [invoiceItem, setInvoiceItem] = useState<InvoiceItemType | null>(null);
  const { alertToast } = useToast();
  const [updateBillLineItem, { isLoading: isUpdatingLineItem }] =
    useUpdateBillOrRequestMutation();

  const handleUpdate = async (invoice_items: InvoiceItem[]) => {
    if (groupId && entityId) {
      try {
        const response = await updateBillLineItem({
          payload: { invoice_items },
          groupId: groupId,
          entityId: entityId,
          billId: bill.uuid,
        }).unwrap();
        setFieldValue("invoice_items", response.invoice_items);
      } catch (error) {
        alertToast(
          { message: (error as BackendError).data?.error?.message },
          error as Error
        );
      }
    }
  };

  const handleItemDelete = (index: number) => {
    const updatedItems = bill?.invoice_items.filter(
      (item, idx) => idx !== index
    );
    handleUpdate(updatedItems || []);
  };

  const handleItemEdit = (item: InvoiceItem) => {
    setInvoiceItem({ ...item, is_new_item: false });
  };

  const handleItemDuplicate = (item: InvoiceItem) => {
    setInvoiceItem({ ...item, uuid: "", is_new_item: true });
    setOverlayActiveItem?.("add-line-item");
  };

  const handleCloseEdit = () => {
    setInvoiceItem(null);
    setOverlayActiveItem && setOverlayActiveItem(null);
  };

  const handleSaveItem = async (item: InvoiceItem) => {
    try {
      if (invoiceItem?.uuid) {
        const updatedItems = bill?.invoice_items.map((existingItem) =>
          existingItem.uuid === invoiceItem.uuid ? item : existingItem
        );
        await handleUpdate(updatedItems || []);
      } else {
        await handleUpdate([...(bill?.invoice_items || []), item]);
      }
    } finally {
      handleCloseEdit();
    }
  };

  const billIdentifiers = [
    ASSET_CATEGORY_IDENTIFIER,
    EXPENSE_CATEGORY_IDENTIFIER,
  ];

  return (
    <InvoiceItemGroup title="Item Details">
      {(bill?.invoice_items?.length ?? 0) > 0 && (
        <LineItemTable
          invoice={bill!}
          handleItemDelete={handleItemDelete}
          handleItemEdit={handleItemEdit}
          handleItemDuplicate={handleItemDuplicate}
          invoiceItem={invoiceItem}
          isUpdatingTableItem={isUpdatingLineItem}
          showCategory={showCategory}
          showTotals={showTotals}
          rootCategoryIdentifiers={billIdentifiers}
          onBulkCategoryUpdate={handleUpdate}
          writePermissionKey={WRITE_PERMISSION_KEYS.EXPENSE}
          renderEditForm={(item) => (
            <LineItemUI
              invoice_item={{ ...item, is_new_item: false }}
              groupId={groupId || ""}
              entityId={entityId || ""}
              currency={bill.currency}
              closeModal={handleCloseEdit}
              onSave={handleSaveItem}
              isUpdating={isUpdatingLineItem}
              rootCategoryIdentifiers={billIdentifiers}
              showTextField={true}
              showCategory={showCategory}
              isBillLineItem={isBillLineItem}
              invoiceDate={bill.bill_date}
            />
          )}
        />
      )}
      <FocusBlock.Item
        placement={FOCUS_BLOCK_PLACEMENT.INSERT}
        value="add-line-item"
      >
        <FocusBlock.Content>
          <LineItemUI
            invoice_item={invoiceItem!}
            groupId={groupId || ""}
            entityId={entityId || ""}
            currency={bill.currency}
            closeModal={handleCloseEdit}
            onSave={handleSaveItem}
            isUpdating={isUpdatingLineItem}
            rootCategoryIdentifiers={billIdentifiers}
            showTextField={true}
            showCategory={showCategory}
            isBillLineItem={isBillLineItem}
            invoiceDate={bill.bill_date}
          />
        </FocusBlock.Content>
        <div>
          <AddLineItemButton
            writePermissionKey={WRITE_PERMISSION_KEYS.EXPENSE}
            onClick={() => {
              setInvoiceItem({
                description: "",
                quantity: 1,
                rate: 0.0,
                uuid: "",
                product_data_id: null,
                billing_period_enabled: false,
                billing_period_start_date: bill.bill_date ?? null,
                billing_period_end_date: null,
                category_id: null,
                is_new_item: true,
              });
            }}
          />
        </div>
      </FocusBlock.Item>
    </InvoiceItemGroup>
  );
};

export const TaxAndDiscount = ({ bill, onChange }: BillConsoleProps) => {
  const { setFieldValue } = useFormikContext<UpdatePayload>();
  return (
    <InvoiceItemGroup title="Tax & Discount">
      <div className="t-flex t-gap-4 sm:t-flex-col sm:t-gap-3">
        <div className="t-flex-1 t-min-w-0">
          {bill.tax_type === "PERCENT" ? (
            <NumericInput
              fieldProps={{ name: "tax" }}
              storeNumeric
              numericProps={{
                customSize: "small",
                fixedDecimalScale: true,
                decimalScale: 2,
                suffix: " %",
                allowNegative: false,
                rightComponent: (
                  <ToggleNumbers
                    value={bill.tax_type}
                    onValueChange={(value) => setFieldValue("tax_type", value)}
                  />
                ),
              }}
              label="Tax"
            />
          ) : (
            <PriceInput
              unit={bill.currency === USD ? "$" : null}
              name="tax"
              customSize="small"
              rightComponent={
                <div className="t-flex t-items-center t-gap-1">
                  {bill.currency !== USD && <span>{bill?.currency}</span>}
                  <ToggleNumbers
                    value={bill.tax_type}
                    onValueChange={(value) => setFieldValue("tax_type", value)}
                  />
                </div>
              }
              label="Tax"
              allowNegative={false}
            />
          )}
        </div>

        <div className="t-flex-1 t-min-w-0">
          {bill.discount_type === "PERCENT" ? (
            <NumericInput
              label="Discount"
              storeNumeric
              fieldProps={{ name: "discount" }}
              numericProps={{
                customSize: "small",
                fixedDecimalScale: true,
                decimalScale: 2,
                suffix: " %",
                allowNegative: false,
                rightComponent: (
                  <ToggleNumbers
                    value={bill.discount_type}
                    onValueChange={(value) =>
                      setFieldValue("discount_type", value)
                    }
                  />
                ),
              }}
            />
          ) : (
            <PriceInput
              unit={bill.currency === USD ? "$" : null}
              name="discount"
              customSize="small"
              allowNegative={false}
              rightComponent={
                <div className="t-flex t-items-center t-gap-1">
                  {bill.currency !== USD && <span>{bill?.currency}</span>}

                  <ToggleNumbers
                    value={bill.discount_type}
                    onValueChange={(value) =>
                      setFieldValue("discount_type", value)
                    }
                  />
                </div>
              }
              label="Discount"
            />
          )}
        </div>

        <div className="t-flex-1 t-min-w-0">
          <PriceInput
            unit={null}
            allowNegative={false}
            name="paid_amount"
            customSize="small"
            label="Advance Paid"
            showCurrencyFlag
            rightComponent={
              <span className="t-text-text-30 t-text-body-sm">
                {bill?.currency}
              </span>
            }
          />
        </div>
      </div>
    </InvoiceItemGroup>
  );
};

export const PaymentDetails = ({
  bill,
  onChange,
  paymentlink = false,
}: BillConsoleProps & {
  paymentlink?: boolean;
}) => {
  const { copyToClipboard } = useCopyToClipboard();

  return (
    <InvoiceItemGroup title="Payment Details">
      <TextArea
        label={
          <div className="t-flex t-items-center t-justify-center t-gap-2">
            Payment Instructions
            <ToolTip
              align="start"
              alignOffset={-10}
              keepOpenOnActivation
              text={
                <div>
                  This will be added as payment instruction
                  <br /> in the invoice.
                </div>
              }
            >
              <span>
                <InfoFilledSmall color="currentColor" size="12" />
              </span>
            </ToolTip>
          </div>
        }
        name="payment_instructions"
        placeholder="Enter payment instructions"
      />
      {paymentlink && (
        <TextInput
          label="Payment Link"
          name="payment_link"
          customSize="small"
          placeholder="https://example.com/"
          rightComponent={
            <Button
              type="button"
              size="small"
              customType="link"
              disabled={!bill?.payment_link}
              onClick={debounce(() =>
                copyToClipboard(bill?.payment_link || "")
              )}
            >
              <span className="t-flex t-items-center t-gap-2">
                Copy
                <CopyIcon />
              </span>
            </Button>
          }
        />
      )}
      <TextArea label="Payment Terms" name="terms" />
    </InvoiceItemGroup>
  );
};
